'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, AlertTriangle, UserCircle, ChevronDown, ExternalLink } from 'lucide-react';

const mainNavItems = [
  { label: 'Être accompagné', href: '/tre-accompagn' },
  { label: 'Ressources', href: '/ressources' },
  { label: 'Actualités', href: '/actualites' },
  { label: 'Aidants', href: '/choisir-mon-parcours' },
];

const associationLinks = [
  { label: 'Notre mission', href: '/#mission' },
  { label: "Comité d\'experts", href: '/notre-mission/comite-experts' },
  { label: 'Témoignages', href: '/temoignages' },
  { label: 'Contact', href: '/#contact' },
];

const soutenirLinks = [
  { label: 'Faire un don', href: 'https://www.helloasso.com/associations/apte-accompagnement-et-prevention-des-traumatismes-emotionnels/formulaires/1', external: true },
  { label: 'Devenir adhérent', href: 'https://www.helloasso.com/associations/apte-accompagnement-et-prevention-des-traumatismes-emotionnels/adhesions/apte-1', external: true },
  { label: 'Devenir mécène', href: '/#soutenir' },
  { label: 'Construire un partenariat', href: '/#soutenir' },
];

// Mobile menu nav order as specified
const mobileNavItems = [
  { label: 'Être accompagné', href: '/tre-accompagn' },
  { label: 'Ressources', href: '/ressources' },
  { label: 'Actualités', href: '/actualites' },
  { label: 'Aidants', href: '/choisir-mon-parcours' },
  { label: 'Comprendre et prévenir', href: '/ressources' },
  { label: 'Notre mission', href: '/#mission' },
  { label: "Comité d\'experts", href: '/notre-mission/comite-experts' },
  { label: 'Témoignages', href: '/temoignages' },
];

function DropdownMenu({
  label,
  items,
  pathname,
}: {
  label: string;
  items: { label: string; href: string; external?: boolean }[];
  pathname: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 min-h-[44px]"
        style={{ color: '#C7D3D8' }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-[#E2E8EC] py-1.5 min-w-[220px] z-50">
          {items.map((item) => (
            item.external ? (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-[#071923] hover:bg-[#0A4C64]/6 hover:text-[#0A4C64] transition-all duration-200 min-h-[44px]"
                onClick={() => setOpen(false)}
              >
                {item.label}
                <ExternalLink size={12} className="text-[#536873] flex-shrink-0" aria-hidden="true" />
              </a>
            ) : (
              <Link
                key={item.label}
                href={item.href}
                className="flex items-center px-4 py-2.5 text-sm text-[#071923] hover:bg-[#0A4C64]/6 hover:text-[#0A4C64] transition-all duration-200 min-h-[44px]"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            )
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      // Focus first element in menu
      setTimeout(() => firstFocusableRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Focus trap + Escape key
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setMobileOpen(false);
      menuButtonRef.current?.focus();
      return;
    }
    if (e.key === 'Tab' && menuPanelRef.current) {
      const focusable = menuPanelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabIndex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  const headerBg = scrolled ? 'rgba(4, 25, 34, 0.97)' : 'rgba(4, 25, 34, 0.82)';

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: headerBg,
          backdropFilter: 'blur(16px) saturate(115%)',
          WebkitBackdropFilter: 'blur(16px) saturate(115%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.10)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
        role="banner"
      >
        {/* ── Utility bar — desktop only ── */}
        <div
          className="hidden lg:block border-b"
          style={{ borderColor: 'rgba(255,255,255,0.07)', minHeight: '34px' }}
        >
          <div
            className="mx-auto flex items-center justify-between gap-4 px-8"
            style={{ maxWidth: '1280px', width: 'calc(100% - 64px)', height: '36px' }}
          >
            <p className="text-xs" style={{ color: '#C5D1D6', fontSize: '13px' }}>
              APTE est une plateforme associative d&apos;information et d&apos;orientation.
            </p>
            <div className="flex items-center gap-4">
              <Link
                href="/#aide-immediate"
                className="flex items-center gap-1.5 text-xs font-medium transition-all duration-200 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40 rounded"
                style={{ color: '#C5D1D6', fontSize: '13px' }}
              >
                <AlertTriangle size={12} aria-hidden="true" />
                Aide immédiate
              </Link>
              <span className="w-px h-3 bg-white/15" aria-hidden="true" />
              <Link
                href="/connexion-et-r-cup-ration-d-acc-s"
                className="flex items-center gap-1.5 text-xs font-medium transition-all duration-200 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40 rounded"
                style={{ color: '#C5D1D6', fontSize: '13px' }}
              >
                <UserCircle size={12} aria-hidden="true" />
                Espace personnel
              </Link>
            </div>
          </div>
        </div>

        {/* ── Main navigation bar ── */}
        <div
          className="mx-auto"
          style={{
            maxWidth: '1280px',
            width: 'min(calc(100% - 28px), 1280px)',
            paddingLeft: '0',
            paddingRight: '0',
          }}
        >
          <div
            className="flex items-center justify-between gap-3"
            style={{ height: 'var(--mobile-header-height)' }}
          >
            {/* Logo */}
            <Link
              href="/"
              className="flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg"
              aria-label="APTE — Retour à l'accueil"
            >
              <div
                className="flex items-center justify-center rounded-lg"
                style={{
                  background: '#F7F8F5',
                  borderRadius: '8px',
                  padding: '6px 8px',
                  width: '104px',
                }}
              >
                <AppLogo
                  src="/assets/images/logo_complet_v1-1785717083770.png"
                  size={88}
                  className="object-contain"
                />
              </div>
            </Link>

            {/* Desktop nav — >= 1280px */}
            <nav
              className="hidden xl:flex items-center gap-0 flex-1 justify-center"
              role="navigation"
              aria-label="Navigation principale"
            >
              {mainNavItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 min-h-[44px] flex items-center"
                  style={{
                    color: pathname === item.href ? '#FFFFFF' : '#C7D3D8',
                    background: pathname === item.href ? 'rgba(255,255,255,0.10)' : 'transparent',
                  }}
                >
                  {item.label}
                </Link>
              ))}
              <DropdownMenu label="L'association" items={associationLinks} pathname={pathname} />
            </nav>

            {/* Desktop actions */}
            <div className="hidden xl:flex items-center gap-2 flex-shrink-0">
              <DropdownMenu label="Soutenir APTE" items={soutenirLinks} pathname={pathname} />
              <Link
                href="/tre-accompagn"
                className="inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-lg transition-all duration-200 min-h-[44px] whitespace-nowrap shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D1A338]/70"
                style={{ background: '#D1A338', color: '#071923', fontSize: '14px' }}
              >
                Demander un accompagnement
              </Link>
            </div>

            {/* Condensed nav 1024–1279px */}
            <nav
              className="hidden lg:flex xl:hidden items-center gap-1 flex-1 justify-center"
              role="navigation"
              aria-label="Navigation principale condensée"
            >
              {mainNavItems.slice(0, 3).map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 min-h-[44px] flex items-center"
                  style={{ color: '#C7D3D8' }}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="hidden lg:flex xl:hidden items-center gap-2 flex-shrink-0">
              <Link
                href="/tre-accompagn"
                className="inline-flex items-center justify-center font-semibold text-xs px-3 py-2.5 rounded-lg transition-all duration-200 min-h-[44px] whitespace-nowrap"
                style={{ background: '#D1A338', color: '#071923' }}
              >
                Demander un accompagnement
              </Link>
            </div>

            {/* Mobile hamburger — < 1024px */}
            <button
              ref={menuButtonRef}
              className="lg:hidden p-2.5 rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 flex-shrink-0"
              style={{
                color: '#C7D3D8',
                minWidth: '44px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu de navigation'}
            >
              {mobileOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile menu — full-screen panel ── */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[60]"
          aria-modal="true"
          role="dialog"
          aria-label="Menu de navigation"
          onKeyDown={handleMenuKeyDown}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(3,24,33,0.6)' }}
            onClick={closeMobile}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            ref={menuPanelRef}
            className="absolute top-0 right-0 bottom-0 flex flex-col overflow-y-auto"
            style={{
              width: 'min(90vw, 380px)',
              background: '#071923',
              borderLeft: '1px solid rgba(255,255,255,0.10)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {/* Panel header */}
            <div
              className="flex items-center justify-between px-5 flex-shrink-0"
              style={{
                height: 'var(--mobile-header-height)',
                paddingTop: 'env(safe-area-inset-top, 0px)',
                borderBottom: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <Link
                href="/"
                onClick={closeMobile}
                aria-label="APTE — Retour à l'accueil"
                className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg"
              >
                <div
                  className="flex items-center justify-center rounded-lg"
                  style={{ background: '#F7F8F5', borderRadius: '8px', padding: '5px 7px', width: '96px' }}
                >
                  <AppLogo
                    src="/assets/images/logo_complet_v1-1785717083770.png"
                    size={82}
                    className="object-contain"
                  />
                </div>
              </Link>
              <button
                ref={firstFocusableRef}
                onClick={closeMobile}
                className="flex items-center justify-center rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 hover:bg-white/10"
                style={{ width: '44px', height: '44px', color: '#C7D3D8', flexShrink: 0 }}
                aria-label="Fermer le menu"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {/* Nav items */}
            <nav className="flex-1 px-4 py-4 flex flex-col gap-1" role="navigation" aria-label="Navigation mobile">
              {mobileNavItems.map((item) => (
                <Link
                  key={item.href + item.label}
                  href={item.href}
                  className="flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                  style={{
                    color: pathname === item.href ? '#FFFFFF' : '#C7D3D8',
                    background: pathname === item.href ? 'rgba(255,255,255,0.10)' : 'transparent',
                    minHeight: '44px',
                  }}
                  onClick={closeMobile}
                >
                  {item.label}
                </Link>
              ))}

              {/* Soutenir APTE section */}
              <div
                className="mt-3 pt-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.10)' }}
              >
                <p
                  className="px-4 mb-2 font-semibold uppercase tracking-wider"
                  style={{ fontSize: '11px', color: 'rgba(255,255,255,0.38)' }}
                >
                  Soutenir APTE
                </p>
                {soutenirLinks.map((item) => (
                  item.external ? (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                      style={{ color: '#A8B8BE', minHeight: '44px' }}
                      onClick={closeMobile}
                    >
                      {item.label}
                      <ExternalLink size={12} aria-hidden="true" />
                    </a>
                  ) : (
                    <Link
                      key={item.label}
                      href={item.href}
                      className="flex items-center px-4 py-3 rounded-lg text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                      style={{ color: '#A8B8BE', minHeight: '44px' }}
                      onClick={closeMobile}
                    >
                      {item.label}
                    </Link>
                  )
                ))}
              </div>

              {/* Access section */}
              <div
                className="mt-3 pt-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.10)' }}
              >
                <Link
                  href="/connexion-et-r-cup-ration-d-acc-s"
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                  style={{ color: '#C7D3D8', minHeight: '44px' }}
                  onClick={closeMobile}
                >
                  <UserCircle size={16} aria-hidden="true" />
                  Espace personnel
                </Link>
                <Link
                  href="/#aide-immediate"
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                  style={{ color: '#FCA5A5', background: 'rgba(127,29,29,0.20)', minHeight: '44px', borderRadius: '8px' }}
                  onClick={closeMobile}
                >
                  <AlertTriangle size={16} aria-hidden="true" />
                  Aide immédiate
                </Link>
              </div>
            </nav>

            {/* Bottom CTA */}
            <div
              className="px-4 py-4 flex-shrink-0"
              style={{ borderTop: '1px solid rgba(255,255,255,0.10)' }}
            >
              <Link
                href="/tre-accompagn"
                className="flex items-center justify-center gap-2 font-semibold text-sm px-4 rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D1A338]/70 w-full"
                style={{ background: '#D1A338', color: '#071923', minHeight: '52px', fontSize: '15px' }}
                onClick={closeMobile}
              >
                Demander un accompagnement
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}